import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import TranslationPopover from "@components/Modules/Translation/TranslationPopover";
import WordPuzzle from "@components/Modules/WordPuzzle/WordPuzzle";
import { getTagsName } from "@elements/GAData/GADataHelper";
import Outbrain from "@elements/OutbrainAds/Outbrain";
import { CAS_UGC_SECTION_BANNER_ID } from "@helper/getEnvVariables";
import { useBHVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useBHVirtualPVAnalyticsCall";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import ArticleElementWrapper from "@pages/Article/Components/ArticleElementWrapper";
import BHArticleBasePage, {
  AdsSettings,
  BHArticleBasePageProps,
} from "@pages/BHArticleBasePage";
import ErrorPage from "@pages/ErrorPage";
import NotFound from "@pages/NotFound";
import UGCArticleInfiniteScroll from "@pages/UGC/UGCArticlePage/UGCArticleInfiniteScroll";
import UGCArticleMetaData from "@pages/UGC/UGCArticlePage/UGCArticleMetaData";
import { PayloadResponseType } from "@pages/UGC/UGCArticlePage/UGCArticlePage.server";
import { MATA_MATA_SECTION } from "@pages/UGC/UGCLandingPage/UGCLandingPage";
import {
  CustomContext,
  TRouteWithRedirect,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { useEffect, useState } from "react";

import UGCArticleContentCardNew from "./UGCArticleLayout/UGCArticleContentCardNew";

export function UgcArticlePage() {
  const routeContext: CustomContext<
    TRouteWithRedirect<PayloadResponseType, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  const [currentArticle, setCurrentArticle] = useState<ProcessedArticleData>();
  const [resolvedGsChannels, setResolvedGsChannels] = useState<string[]>([]);
  const [pagination, setPagination] = useState<number>(0);
  const { gsChannels, isLoading } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );
  const analyticsCall = useBHVirtualPVAnalyticsCall();

  // update when gsChannels are loaded
  useEffect(() => {
    if (!isLoading && gsChannels.length) {
      console.log("[gs_channel]", gsChannels);
      setResolvedGsChannels(gsChannels);
    }
  }, [isLoading, gsChannels]);

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message={dataLoaderResponse.payload ?? ""} />;
  }

  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const mainArticle = dataLoaderResponse.payload.article;

    const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;

    const handleNewArticle = (
      newArticle: ProcessedArticleData,
      pageNumber: number,
      viewPortChange: boolean,
    ) => {
      setCurrentArticle(newArticle);
      setPagination(pageNumber);
      if (viewPortChange) {
        setTimeout(() => {
          analyticsCall(
            newArticle.title,
            undefined,
            undefined,
            undefined,
            pageNumber,
          );
        }, 300);
      }
    };

    const { gaData, metaTags } = UGCArticleMetaData({
      article: currentArticle || mainArticle,
      pageNumber: pagination,
      gsChannels:
        currentArticle && currentArticle.gsChannels
          ? currentArticle.gsChannels
          : resolvedGsChannels,
    });

    const bhTags = getTagsName(mainArticle.tags || []);
    const bhArticleId = mainArticle.id;
    const articleSlotTarget: AdTargetingType<ArticleAdTargetKeys, string>[] = [
      { key: ArticleAdTargetKeys.ARTICLE_ID, value: bhArticleId },
      { key: ArticleAdTargetKeys.TAGS, value: bhTags.toLowerCase() },
    ];

    const basePageProps: BHArticleBasePageProps = {
      adsUniqueSectionName: MATA_MATA_SECTION,
      pageAdTargetValue,
      gaData,
      metaTags,
      hasLb1Ads: true,
      gsChannels: resolvedGsChannels,
      articleSlotTarget,
      prestitialPageTargeting: pageAdTargetValue,
    };

    const rednerArticlePage = (isOutbrainEnabled: boolean) => {
      return (
        <>
          <div className="bg-white-200 flex flex-col xl:flex-row items-center justify-center mb-5 py-md min-h-[200px] lg:min-h-[220px]">
            <CASBlock casId={CAS_UGC_SECTION_BANNER_ID} />
          </div>
          <div id={mainArticle.id} className="article-card mt-5">
            <UGCArticleContentCardNew
              mainArticle={mainArticle}
              pageAdTargetValue={pageAdTargetValue}
              pageNumber={0}
              isInfiniteScrollArticle={false}
              gsChannels={resolvedGsChannels}
              articleSlotTarget={articleSlotTarget}
            />
            <ArticleElementWrapper>
              <div className="mt-4 lg:mt-6 border-t-2 border-blue-400">
                {isOutbrainEnabled && mainArticle.urlPath && (
                  <Outbrain pathName={mainArticle.urlPath} />
                )}
              </div>
            </ArticleElementWrapper>
          </div>
        </>
      );
    };

    return (
      <>
        <BHArticleBasePage {...basePageProps}>
          {(adsSettings: AdsSettings) => (
            <>
              {rednerArticlePage(adsSettings.isOutbrainEnabled || false)}
              <UGCArticleInfiniteScroll
                mainArticle={mainArticle}
                pageAdTargetValue={pageAdTargetValue}
                onNewArticle={handleNewArticle}
                isOutbrainEnabled={adsSettings.isOutbrainEnabled || false}
              />
              <TranslationPopover
                gsChannels={resolvedGsChannels}
                articleSlotTarget={articleSlotTarget}
                pageAdTargetType={pageAdTargetValue}
              />
            </>
          )}
        </BHArticleBasePage>
        <WordPuzzle />
      </>
    );
  }
}
